// services/driverJobService.js
// Authority layer for Hauler app job state transitions.
// Phase 1 step 1.5.
//
// Callers provide universal context (requestId, driverUserId, completion data).
// Service reads contact phone, driver name, and job context from the database.
// Eliminates caller-drift bugs (e.g. DriverMapScreen sending session.user.email
// as driver_name to customers instead of driverProfile.first_name).
//
// Five functions: claimJob, releaseJob, markEnRoute, markArrived, completeJob.
// claimJob and releaseJob delegate to supabase.js (which handles SMS internally).
// markEnRoute, markArrived, and completeJob own their full side-effect chain.
//
// Original IP -- LoadDash Services LLC

import { supabase } from "./supabaseService";
import {
  claimJob as supabaseClaimJob,
  releaseJob as supabaseReleaseJob,
  completeJob as supabaseCompleteJob,
} from "./supabaseService";
import { sendSMSNotification } from "./notificationService";
import { trackEvent, EVENTS } from "../analytics/eventTracker";

// ── Claim ────────────────────────────────────────────────────────
// Thin delegate. supabase.claimJob already handles:
//   - Atomic UPDATE with status=available guard
//   - Server-side trigger validation (capacity, trailer, crew)
//   - Customer SMS (loader_assigned_customer)
//   - Driver SMS (driver_assigned)
//   - driver_notified_at + loader_first_name UPDATE

export async function claimJob({ requestId, driverUserId, crewNames = [] }) {
  return supabaseClaimJob(requestId, driverUserId, { crewNames });
}

// ── Release ──────────────────────────────────────────────────────
// Thin delegate. supabase.releaseJob already handles:
//   - Atomic UPDATE with status=claimed guard
//   - Performance event (cancel_after_accept, -15 delta)

export async function releaseJob({ requestId, driverUserId }) {
  return supabaseReleaseJob(requestId, driverUserId);
}

// ── Mark En Route ────────────────────────────────────────────────
// NEW logic. Replaces inline supabase.from('requests').update(...)
// in DriverScreen.handleMarkEnRoute and DriverMapScreen.handlePickupAction.
//
// Reads contact phone and driver first name from the database so callers
// never need to pass user-facing data they might get wrong.

export async function markEnRoute({ requestId, driverUserId }) {
  const now = new Date().toISOString();

  // Step 1: Read job phone for SMS
  const { data: jobRow, error: jobErr } = await supabase
    .from("requests")
    .select("phone, addr, sms_consent")
    .eq("id", requestId)
    .single();

  if (jobErr) {
    console.warn("[driverJobService.markEnRoute] Job read failed:", jobErr.message);
  }

  // Step 2: Read driver first name for SMS
  const { data: profileRow, error: profileErr } = await supabase
    .from("driver_profiles")
    .select("first_name")
    .eq("user_id", driverUserId)
    .single();

  if (profileErr) {
    console.warn("[driverJobService.markEnRoute] Profile read failed:", profileErr.message);
  }

  // Step 3: Status transition (atomic, guarded by status=claimed + assigned_driver)
  const { data, error } = await supabase
    .from("requests")
    .update({ status: "en_route", en_route_at: now })
    .eq("id", requestId)
    .eq("assigned_driver", driverUserId)
    .eq("status", "claimed")
    .select("id");

  if (error) throw new Error("EN_ROUTE_FAILED: " + error.message);
  if (!data || data.length === 0) {
    throw new Error("STATE_TRANSITION_REJECTED: claimed -> en_route failed (wrong status or driver)");
  }

  // Step 4: SMS dispatch (fail-loud-but-not-blocking)
  let smsSkipped = false;
  let smsReason = null;
  const driverName = profileRow?.first_name || "Your hauler";

  if (jobRow?.phone && jobRow?.sms_consent) {
    sendSMSNotification("en_route_customer", jobRow.phone, {
      driver_name: driverName,
      address: jobRow.addr,
      request_id: requestId,
    }, requestId, "neighbor").catch((e) => {
      console.warn("[driverJobService.markEnRoute] SMS failed (non-fatal):", e?.message);
    });
  } else if (jobRow?.phone) {
    smsSkipped = true;
    smsReason = "sms_consent_not_granted";
  } else {
    console.warn("[driverJobService.markEnRoute] No contact phone for request", requestId);
    smsSkipped = true;
    smsReason = "no_contact_phone";
  }

  // Step 5: Analytics
  trackEvent("job_en_route", driverUserId, { request_id: requestId });

  return { ok: true, smsSkipped, reason: smsReason };
}

// ── Mark Arrived ─────────────────────────────────────────────────
// NEW logic. Replaces inline supabase.from('requests').update(...)
// in DriverScreen.handleMarkArrived and DriverMapScreen.handlePickupAction.
// No SMS, no driver profile read.

export async function markArrived({ requestId, driverUserId }) {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from("requests")
    .update({ status: "in_progress", in_progress_at: now })
    .eq("id", requestId)
    .eq("assigned_driver", driverUserId)
    .eq("status", "en_route")
    .select("id");

  if (error) throw new Error("ARRIVED_FAILED: " + error.message);
  if (!data || data.length === 0) {
    throw new Error("STATE_TRANSITION_REJECTED: en_route -> in_progress failed (wrong status or driver)");
  }

  // Analytics
  trackEvent("job_arrived", driverUserId, { request_id: requestId });

  return { ok: true };
}

// ── Complete Job ─────────────────────────────────────────────────
// Absorbs SMS + analytics from DriverScreen.handleCompleteJob.
// Delegates DB UPDATE + performance RPCs to supabase.completeJob.
//
// Caller provides verification data. Service handles misrep SMS and
// JOB_VERIFIED analytics that were previously in the caller.

export async function completeJob({
  requestId,
  driverUserId,
  providerVerifiedLeu,
  confirmedStaging,
  flags = [],
  systemAssignedLeu,
  systemAssignedTier,
  driverVerifiedTier,
  completionPhotoUrl,
  predictedLeuSnapshot,
  companyMarginAmt,
  driverPayAmt,
  variableCostAmt,
  marketId,
  zoneCostSnapshot,
}) {
  // Delegate to supabase.completeJob for the DB UPDATE + performance events
  const data = await supabaseCompleteJob({
    requestId,
    providerVerifiedLeu,
    confirmedStaging,
    flags,
    systemAssignedLeu,
    systemAssignedTier,
    driverVerifiedTier,
    completionPhotoUrl,
    completedBy: driverUserId,
    predictedLeuSnapshot,
    companyMarginAmt,
    driverPayAmt,
    variableCostAmt,
    marketId,
    zoneCostSnapshot,
  });

  let smsSkipped = false;
  let smsReason = null;

  // Read job contact + address context once, used for both the customer
  // completion SMS and the admin misrep alert below.
  const { data: jobRow } = await supabase
    .from("requests")
    .select("addr, phone, sms_consent")
    .eq("id", requestId)
    .single();

  // ── Customer completion SMS ──────────────────────────────────────
  // Final message in the status sequence (accepted -> on the way ->
  // complete). Consent-gated and non-blocking, same pattern as markEnRoute.
  if (jobRow?.phone && jobRow?.sms_consent) {
    const { data: profileRow } = await supabase
      .from("driver_profiles")
      .select("first_name")
      .eq("user_id", driverUserId)
      .single();

    sendSMSNotification("complete_customer", jobRow.phone, {
      driver_name: profileRow?.first_name || null,
      address: jobRow.addr,
      request_id: requestId,
    }, requestId, "neighbor").catch((e) => {
      console.warn("[driverJobService.completeJob] Completion SMS failed (non-fatal):", e?.message);
    });
  } else if (jobRow?.phone) {
    smsSkipped = true;
    smsReason = "sms_consent_not_granted";
  }

  // Misrep SMS (absorbs logic from DriverScreen.handleCompleteJob lines 585-591)
  if (flags.length > 0) {
    // Import ADMIN_PHONE at call time to avoid circular dependency
    const { ADMIN_PHONE } = await import("../constants");

    if (ADMIN_PHONE) {
      sendSMSNotification("misrep_alert", ADMIN_PHONE, {
        address: jobRow?.addr,
        size: systemAssignedTier,
        verified_size: driverVerifiedTier,
        flags: flags.join(", "),
        request_id: requestId,
      }, null, "operations").catch((e) => {
        console.warn("[driverJobService.completeJob] Misrep SMS failed (non-fatal):", e?.message);
      });
    } else {
      console.warn("[driverJobService.completeJob] ADMIN_PHONE not set, misrep SMS skipped");
      smsSkipped = true;
      smsReason = "no_admin_phone";
    }

    trackEvent(EVENTS.MISREP_FLAGGED, driverUserId, {
      request_id: requestId,
      flags,
      verified_tier: driverVerifiedTier,
      booked_tier: systemAssignedTier,
    });
  }

  // JOB_VERIFIED analytics (always fires, not just on misrep)
  const varianceLeu = providerVerifiedLeu != null && systemAssignedLeu != null
    ? providerVerifiedLeu - systemAssignedLeu
    : null;

  trackEvent(EVENTS.JOB_VERIFIED, driverUserId, {
    request_id: requestId,
    verified_tier: driverVerifiedTier,
    booked_tier: systemAssignedTier,
    variance_leu: varianceLeu,
  });

  // ── Payout trigger (Phase 2) — the ONLY client call site ─────────
  // Fire the server-authoritative payout for the base amount. Non-fatal
  // by design: the completion itself already succeeded above, and the
  // payout-transfer function is idempotent (unique request_id ledger),
  // so a network blip here just means the payout is picked up on retry.
  // The server re-derives every amount from the DB — nothing money-
  // related is sent from here except the request id.
  let payout = null;
  try {
    const { data: { session: activeSession } } = await supabase.auth.getSession();
    if (activeSession?.access_token) {
      const { data: payoutData, error: payoutErr } = await supabase.functions.invoke("payout-transfer", {
        body: { request_id: requestId },
        headers: { Authorization: `Bearer ${activeSession.access_token}` },
      });
      if (payoutErr || payoutData?.error) {
        console.warn("[driverJobService.completeJob] payout deferred:", payoutData?.error || payoutErr?.message);
      } else {
        payout = payoutData;
      }
    }
  } catch (e) {
    console.warn("[driverJobService.completeJob] payout deferred (non-fatal):", e?.message);
  }

  return { data, smsSkipped, reason: smsReason, payout };
}

/**
 * Read customer add-ons for a job (declared before the pickup-day cutoff).
 * RLS grants the assigned Hauler read access; returns [] on any failure so
 * the job screen never blocks on this.
 */
export async function getBookingAddons(requestId) {
  if (!requestId) return [];
  const { data, error } = await supabase
    .from("booking_addons")
    .select("description, added_mattress_pieces, created_at")
    .eq("request_id", requestId)
    .order("created_at", { ascending: true });
  if (error) return [];
  return data || [];
}
